const wpOrigin = (): string => process.env.NEXT_PUBLIC_WORDPRESS_URL || '';
const frontendOrigin = (): string => process.env.NEXT_PUBLIC_FRONTEND_URL || '';

/**
 * Rewrites an absolute WP media URL to a same-origin /api/wp-media proxy path
 * so the browser requests it through our cacheable proxy route instead of
 * hitting the WP Engine origin directly. Non-WP URLs pass through unchanged.
 */
export function toProxiedWpUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  const origin = wpOrigin();
  if (!origin || !url.startsWith(origin)) return url;
  // Encode the path so unsafe characters (e.g. spaces in filenames) survive as a
  // valid URL. encodeURI preserves "/" and query delimiters while escaping spaces.
  return `/api/wp-media${encodeURI(url.slice(origin.length))}`;
}

/**
 * Like toProxiedWpUrl but returns an absolute URL, for contexts that require
 * one (e.g. og:image / twitter:image social meta tags, which crawlers won't
 * resolve from a relative path). WP media URLs are proxied and prefixed with
 * the frontend origin; non-WP URLs pass through unchanged.
 */
export function toAbsoluteProxiedWpUrl(url?: string | null): string | undefined {
  const proxied = toProxiedWpUrl(url);
  if (!proxied || !proxied.startsWith('/api/wp-media')) return proxied;
  return `${frontendOrigin()}${proxied}`;
}

/**
 * Applies toProxiedWpUrl to every URL in a WPGraphQL srcSet string
 * (comma-separated "url widthw" pairs), preserving width descriptors.
 */
export function toProxiedWpSrcSet(srcSet?: string | null): string | undefined {
  if (!srcSet) return undefined;
  return srcSet
    .split(',')
    .map((entry) => {
      const trimmed = entry.trim();
      // The width/density descriptor is the trailing token; the URL is everything
      // before it and may itself contain spaces (e.g. filenames with spaces), so
      // split off the descriptor from the end rather than the first whitespace.
      const match = trimmed.match(/\s+(\d+(?:\.\d+)?[wx])$/);
      const url = match ? trimmed.slice(0, match.index) : trimmed;
      const descriptor = match ? match[1] : undefined;
      const proxied = toProxiedWpUrl(url);
      return descriptor ? `${proxied} ${descriptor}` : proxied;
    })
    .join(', ');
}
